import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, XMarkIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const REASON_COPY = {
    limit_reached: {
        heading: 'Daily Optimizer Limit Reached',
        body: 'Free accounts can use the AI Resume Optimizer 5 times per day. Upgrade to Premium for unlimited optimizations, plus PDF resume downloads.',
    },
    download_locked: {
        heading: 'PDF Download is a Premium Feature',
        body: 'Upgrade to Premium to download your resume as a polished PDF — plus get unlimited AI optimizations every day.',
    },
};

const DEFAULT_COPY = {
    heading: 'Upgrade to Premium',
    body: 'Get unlimited AI resume optimizations and PDF downloads with a Premium membership.',
};

const UpgradeModal = ({ open, onClose, reason }) => {
    const navigate = useNavigate();
    const copy = REASON_COPY[reason] || DEFAULT_COPY;

    const handleUpgrade = () => {
        onClose();
        navigate('/upgrade');
    };

    return (
        <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
            <Dialog.Portal>
                {/* Overlay */}
                <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

                {/* Content */}
                <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-[#0b0e14] border border-white/10 p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-200 focus:outline-none">

                    {/* Close button */}
                    <Dialog.Close asChild>
                        <button
                            className="absolute right-4 top-4 rounded-md text-neutral-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500/60"
                            aria-label="Close"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </Dialog.Close>

                    {/* Icon */}
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-cyan-500/15 border border-cyan-500/30 mb-4">
                        <LockClosedIcon className="h-6 w-6 text-cyan-300" />
                    </div>

                    {/* Title */}
                    <Dialog.Title className="text-lg font-bold text-white mb-2">
                        {copy.heading}
                    </Dialog.Title>

                    {/* Description */}
                    <Dialog.Description className="text-sm text-neutral-400 mb-6 leading-relaxed">
                        {copy.body}
                    </Dialog.Description>

                    {/* Premium benefits list */}
                    <ul className="space-y-2 mb-6">
                        {[
                            'Unlimited AI resume optimizations',
                            'PDF resume download',
                            'Priority support',
                        ].map((benefit) => (
                            <li key={benefit} className="flex items-center gap-2 text-sm text-neutral-300">
                                <SparklesIcon className="h-4 w-4 text-cyan-300 flex-shrink-0" />
                                {benefit}
                            </li>
                        ))}
                    </ul>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2 px-4 rounded-lg text-sm font-medium text-neutral-200 bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                        >
                            Maybe later
                        </button>
                        <button
                            onClick={handleUpgrade}
                            className="flex-1 py-2 px-4 rounded-lg text-sm font-bold text-black bg-cyan-500 hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/10"
                        >
                            Upgrade to Premium
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default UpgradeModal;
